import React from 'react';
import {
  Controller,
  type Control,
  type UseFormRegister,
} from 'react-hook-form';
import Label from 'src/modules/shared/components/atoms/inputs/label';
import Select from 'src/modules/shared/components/atoms/inputs/select';
import TextInput from 'src/modules/shared/components/atoms/inputs/text-input';
import type { I18nContextValue } from 'src/modules/shared/types/i18n/i18n.type';
import { PropertyEditorSectionCard } from './property-editor-section-card';
import type { PropertyFormInputValues } from '../../validations/property-validations';
import type { PropertyEditorCurrencyOption } from '../../utils/property-editor-form.util';

/**
 * Props for the pricing editor section.
 */
interface PropertyEditorPricingSectionProps {
  /** Form control instance for controlled inputs. */
  control: Control<PropertyFormInputValues>;
  /** Form register function for native inputs. */
  register: UseFormRegister<PropertyFormInputValues>;
  /** Currency options offered by the pricing selectors. */
  currencyOptions: PropertyEditorCurrencyOption[];
  /** Whether currency options are still loading. */
  currencyOptionsLoading?: boolean;
  /** Whether sale pricing inputs should be displayed. */
  showSalePricing: boolean;
  /** Whether rent pricing inputs should be displayed. */
  showRentPricing: boolean;
  /** Translation function scoped to the current locale. */
  t: I18nContextValue['t'];
}

/**
 * Pricing section for the property editor.
 */
export const PropertyEditorPricingSection: React.FC<
  PropertyEditorPricingSectionProps
> = ({
  control,
  register,
  currencyOptions,
  currencyOptionsLoading = false,
  showSalePricing,
  showRentPricing,
  t,
}) => {
  const currencySelectDisabled =
    currencyOptionsLoading || currencyOptions.length === 0;

  return (
    <PropertyEditorSectionCard
      title={t('catalog.properties.editor.sections.pricing')}
      description={t('catalog.properties.editor.sections.pricingDescription')}
    >
      <div
        className={`grid grid-cols-1 gap-4 ${
          showSalePricing && showRentPricing ? 'md:grid-cols-2' : ''
        }`}
      >
        {showSalePricing ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1">
              <Label size="md">
                {t('catalog.properties.form.salePriceAmount')}
              </Label>
              <TextInput
                {...register('salePriceAmount')}
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                placeholder="0"
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label size="md">
                {t('catalog.properties.form.salePriceCurrency')}
              </Label>
              <Controller
                control={control}
                name="salePriceCurrency"
                render={({ field: { onChange, value } }) => (
                  <Select
                    options={currencyOptions}
                    value={value ?? ''}
                    loading={currencyOptionsLoading}
                    disabled={currencySelectDisabled}
                    onChange={(event) => onChange(event.target.value)}
                  />
                )}
              />
            </div>
          </div>
        ) : null}

        {showRentPricing ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1">
              <Label size="md">
                {t('catalog.properties.form.rentPriceAmount')}
              </Label>
              <TextInput
                {...register('rentPriceAmount')}
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                placeholder="0"
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label size="md">
                {t('catalog.properties.form.rentPriceCurrency')}
              </Label>
              <Controller
                control={control}
                name="rentPriceCurrency"
                render={({ field: { onChange, value } }) => (
                  <Select
                    options={currencyOptions}
                    value={value ?? ''}
                    loading={currencyOptionsLoading}
                    disabled={currencySelectDisabled}
                    onChange={(event) => onChange(event.target.value)}
                  />
                )}
              />
            </div>
          </div>
        ) : null}
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="flex flex-col gap-1">
          <Label size="md">
            {t('catalog.properties.form.commonExpensesAmount')}
          </Label>
          <TextInput
            {...register('commonExpensesAmount')}
            type="number"
            min="0"
            step="0.01"
            inputMode="decimal"
            placeholder="0"
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label size="md">{t('catalog.properties.form.cadastralId')}</Label>
          <TextInput {...register('cadastralId')} placeholder="12345-6789" />
        </div>
      </div>
    </PropertyEditorSectionCard>
  );
};
